/**
 * ImageGenerationPage Component
 *
 * Main page for AI image generation and editing using Fooocus.
 * Includes secondary navigation for different image operations organized by category.
 *
 * Categories:
 * - Generate: Text to Image, Image Prompt, Describe
 * - Edit: Inpaint, Outpaint, AI Edit
 * - Enhance: Upscale & Vary, Remove BG, Enhance
 * - Creative: Face Swap, Canvas
 */

import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Palette, Wand2, Images } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useImageGeneration } from '../../hooks/useImageGeneration';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTokenGate } from '../../hooks/useTokenGate';
import { isInsufficientTokensFailure, isAuthRequiredFailure } from '../../utils/httpUtils';
import { IMAGE_GENERATION_OPERATION_LABEL_KEY } from '../../constants/imageSource';
import { ToastContainer } from '../../components/Toast';
import { LoginModal } from '../../components/auth';
import { SubTabBar, type SubTabItem } from '../../components/common';
import { ImageHero } from './ImageHero';
import { ImageTabContent } from './ImageTabContent';
import { ImageProgress } from './ImageProgress';
import { ImageResults } from './ImageResults';
import { ImageFeatures } from './ImageFeatures';
import type { ImageTab } from './types';
import type { CostGate } from '../../utils/costGate';

export const ImageGenerationPage = memo(function ImageGenerationPage() {
  const { t } = useTranslation();
  useAuth(); // Initialize auth context
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth, openLoginModal } =
    useRequireAuth();
  const { reportInsufficientTokens } = useTokenGate();
  const {
    config,
    setConfig,
    isGenerating,
    progress,
    startedAt,
    expectedSeconds,
    mayColdStart,
    result,
    error,
    failure,
    generateImage,
    reset,
  } = useImageGeneration();
  const { toasts, showToast, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState<ImageTab>('generate');
  // Last quote from the cost card, so a server-side 402 can say how many tokens
  // the refused request needed (see ImageConfigForm's onEstimateChange doc).
  const [estimatedTokens, setEstimatedTokens] = useState(0);

  const imageOperationName = t(IMAGE_GENERATION_OPERATION_LABEL_KEY, {
    defaultValue: 'AI Image Generation',
  });

  const handleEstimateChange = useCallback((estimate: CostGate) => {
    setEstimatedTokens(estimate.tokens);
  }, []);

  // A failure the SERVER classified. Two of its outcomes are not "generation
  // failed" and must not be shown as one:
  //   - out of tokens  -> the purchase modal, with the quote this page showed;
  //   - no credential  -> the sign-in modal.
  // Everything else keeps the existing error toast.
  const isInsufficientTokens = failure !== null && isInsufficientTokensFailure(failure);
  const isAuthRequired = failure !== null && isAuthRequiredFailure(failure);

  // Standardized top sub-navigation. The former Edit / Enhance / Creative
  // sub-tabs are consolidated under the model-first "Image Edit" tab.
  const imageTabs = useMemo<SubTabItem[]>(
    () => [
      {
        id: 'generate',
        label: t('image.tabs.generate', { defaultValue: 'Text to Image' }),
        icon: Palette,
      },
      {
        id: 'image-edit',
        label: t('image.tabs.imageEdit', { defaultValue: 'Image Edit' }),
        icon: Wand2,
      },
      {
        id: 'posts',
        label: t('image.tabs.posts', { defaultValue: 'Posts & Carousels' }),
        icon: Images,
      },
    ],
    [t]
  );

  // Handle generation with authentication
  const handleGenerate = useCallback(async () => {
    requireAuth(
      async () => {
        showToast(
          'info',
          t('image.notifications.startingTitle', { defaultValue: 'Starting Generation' }),
          t('image.notifications.startingMessage', {
            defaultValue: 'Your image is being generated...',
          }),
          3000
        );
        await generateImage();
      },
      t('auth.loginRequired.image', { defaultValue: 'Please log in to generate images' })
    );
  }, [requireAuth, showToast, t, generateImage]);

  // Handle using a prompt from the describe tab
  const handleUsePrompt = useCallback(
    (prompt: string) => {
      setConfig({ ...config, prompt });
      setActiveTab('generate');
    },
    [config, setConfig]
  );

  // Show error toast when generation fails.
  //
  // The two classified outcomes are handled below instead: a raw "Generation
  // Failed" toast carrying the backend's prose is exactly what told a customer
  // who had simply run out of tokens to go configure an API key.
  useEffect(() => {
    if (error && !isInsufficientTokens && !isAuthRequired) {
      showToast(
        'error',
        t('image.notifications.errorTitle', { defaultValue: 'Generation Failed' }),
        error
      );
    }
  }, [error, isInsufficientTokens, isAuthRequired, showToast, t]);

  // Out of tokens -> the ONE purchase surface (App-level InsufficientTokensModal).
  //
  // Keyed on `failure` (a fresh object per refusal), NOT on the boolean derived
  // from it: after the user closes the modal and presses Generate again, a
  // second refusal leaves the boolean at `true`, so an effect watching the
  // boolean would not re-run and the second refusal would be silent.
  useEffect(() => {
    if (failure !== null && isInsufficientTokensFailure(failure)) {
      reportInsufficientTokens(estimatedTokens > 0 ? estimatedTokens : null, imageOperationName);
    }
    // `estimatedTokens` is read at the moment of the refusal — it must not be a
    // dependency, or every re-quote would reopen a modal the user just closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failure, reportInsufficientTokens, imageOperationName]);

  // No usable credential -> sign in. Reached when a session expires mid-session,
  // and on the desktop path where the backend refuses to bill cloud work with no
  // forwarded token (the pre-flight requireAuth below covers the signed-out case).
  useEffect(() => {
    if (failure !== null && isAuthRequiredFailure(failure)) {
      openLoginModal(
        t('auth.loginRequired.image', { defaultValue: 'Please log in to generate images' })
      );
    }
  }, [failure, openLoginModal, t]);

  // Show success toast when generation completes
  useEffect(() => {
    if (result?.images && result.images.length > 0) {
      showToast(
        'success',
        t('image.notifications.successTitle', { defaultValue: 'Images Ready!' }),
        t('image.notifications.successMessage', {
          count: result.images.length,
          defaultValue: 'Successfully generated {{count}} images',
        })
      );
    }
  }, [result, showToast, t]);

  // What the inline error box under the form shows. For a classified refusal
  // the backend's prose is replaced: "…this installation holds no
  // OPENROUTER_API_KEY. Set your own in Settings > API Keys" is advice for an
  // advanced user, not for the ordinary customer who is short on balance and is
  // already being offered the packages.
  const displayError = isInsufficientTokens
    ? t('tokens.insufficientTokens', { defaultValue: 'Insufficient Tokens' })
    : error;

  const showContent = !isGenerating && !result;
  const showResults = result && result.images && result.images.length > 0 && !isGenerating;

  return (
    <main className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-testid="image-generation-page">
      {/* Hero Section - Only show when not generating and no result */}
      {showContent && <ImageHero />}

      {/* Standardized top sub-navigation (full-width) */}
      {showContent && (
        <SubTabBar
          tabs={imageTabs}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as ImageTab)}
          accent="blue"
          className="mb-6 rounded-lg"
          ariaLabel={t('image.subnav.ariaLabel', { defaultValue: 'Image tools' })}
        />
      )}

      {/* Content Area (full-width, below the tab bar) */}
      <div className="min-w-0 space-y-6">
        {/* Tab Content */}
        {showContent && (
          <ImageTabContent
            activeTab={activeTab}
            config={config}
            onConfigChange={setConfig}
            onSubmit={handleGenerate}
            isLoading={isGenerating}
            error={displayError}
            onUsePrompt={handleUsePrompt}
            onEstimateChange={handleEstimateChange}
          />
        )}

        {/* Progress Indicator */}
        {isGenerating && (
          <ImageProgress
            progress={progress}
            startedAt={startedAt}
            expectedSeconds={expectedSeconds}
            mayColdStart={mayColdStart}
          />
        )}

        {/* Results Display */}
        {showResults && (
          <ImageResults
            images={result.images}
            processingTime={result.processingTime}
            onReset={reset}
          />
        )}

        {/* Features Section - Only show when not generating, no result, and on generate tab */}
        {showContent && activeTab === 'generate' && <ImageFeatures />}
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </main>
  );
});

export default ImageGenerationPage;
